<template>
	<div class="modal-drag" :style="{ zIndex: indexModalIndex }">
		<modal
			name="project-file-selector"
			:style="{ zIndex: indexModalIndex }"
			hide-overlay
			draggable=".drag-header"
			:clickToClose="true"
			@before-close="closeModal"
			:focusTrap="true"
			:adaptive="true"
			height="auto"
			width="520"
		>
			<v-card class="file-selector-card" elevation="0">
				<!-- Header -->
				<v-card-title class="pa-0 drag-header">
					<div class="header-bar d-flex flex-row align-center justify-space-between px-6 py-4 w-100">
						<div class="d-flex flex-row align-center">
							<div class="header-icon-wrap mr-3">
								<v-icon color="#524D4D" size="20">mdi-folder-open</v-icon>
							</div>
							<div>
								<p class="mb-0 font-weight-bold header-title">Seleccionar Archivo</p>
								<p class="mb-0 header-subtitle" v-if="!loadingFiles">
									{{ files.length }} archivo{{ files.length !== 1 ? 's' : '' }} disponible{{ files.length !== 1 ? 's' : '' }}
								</p>
							</div>
						</div>
						<v-btn icon small class="close-btn" @click="closeModal">
							<v-icon size="18">mdi-close</v-icon>
						</v-btn>
					</div>
				</v-card-title>

				<v-divider class="header-divider" />

				<v-card-text class="pa-0">
					<div class="content-area">
						<!-- Search field -->
						<div class="search-wrap px-6 pt-5 pb-3">
							<v-text-field
								v-model="searchQuery"
								placeholder="Buscar archivos..."
								prepend-inner-icon="mdi-magnify"
								dense
								outlined
								hide-details
								clearable
								class="search-field"
								background-color="white"
								:loading="loadingFiles"
								@input="debouncedSearch"
								@click:clear="clearSearch"
							/>
						</div>

						<!-- File list -->
						<div class="file-list-wrap px-4 pb-2">
							<!-- Loading skeleton -->
							<template v-if="loadingFiles">
								<div v-for="i in 4" :key="'skel-' + i" class="skeleton-item d-flex align-center px-3 py-3 mb-1">
									<div class="skeleton-icon mr-3" />
									<div class="flex-grow-1">
										<div class="skeleton-line mb-1" style="width: 60%" />
										<div class="skeleton-line" style="width: 35%" />
									</div>
								</div>
							</template>

							<!-- Empty state -->
							<template v-else-if="files.length === 0">
								<div class="empty-state d-flex flex-column align-center justify-center py-10">
									<div class="empty-icon-wrap mb-3">
										<v-icon size="36" color="#C5C0C0">{{ searchQuery ? 'mdi-file-search-outline' : 'mdi-folder-open-outline' }}</v-icon>
									</div>
									<p class="empty-title mb-1">
										{{ searchQuery ? 'Sin resultados' : 'No hay archivos' }}
									</p>
									<p class="empty-subtitle">
										{{ searchQuery ? `No se encontraron archivos para "${searchQuery}"` : 'Este proyecto no tiene archivos disponibles' }}
									</p>
								</div>
							</template>

							<!-- Files -->
							<template v-else>
								<v-list dense class="file-list pa-0">
									<v-list-item-group v-model="selectedFileIndex" color="primary">
										<v-list-item
											v-for="(file, index) in files"
											:key="file.id"
											class="file-item mb-1"
											:class="{ 'file-item--selected': selectedFileIndex === index }"
										>
											<v-list-item-icon class="my-auto mr-3">
												<div class="file-icon-wrap" :class="getFileIconClass(file.name)">
													<v-icon size="16" :color="getFileIconColor(file.name)">
														{{ getFileIcon(file.name) }}
													</v-icon>
												</div>
											</v-list-item-icon>
											<v-list-item-content>
												<v-list-item-title class="file-name">{{ file.name }}</v-list-item-title>
												<v-list-item-subtitle class="file-meta d-flex align-center">
													<span v-if="file.updated_at">{{ formatDate(file.updated_at) }}</span>
												</v-list-item-subtitle>
											</v-list-item-content>
											<v-list-item-action v-if="selectedFileIndex === index">
												<v-icon size="16" color="#524D4D">mdi-check-circle</v-icon>
											</v-list-item-action>
										</v-list-item>
									</v-list-item-group>
								</v-list>
							</template>
						</div>
					</div>

					<!-- Footer -->
					<div class="footer-bar px-6 py-4 d-flex flex-row justify-space-between align-center">
						<p class="mb-0 footer-hint" v-if="selectedFileIndex !== null && files[selectedFileIndex]">
							<v-icon size="13" color="#888">mdi-check-circle-outline</v-icon>
							<span class="ml-1">{{ files[selectedFileIndex].name }}</span>
						</p>
						<p class="mb-0 footer-hint" v-else>Selecciona un archivo de la lista</p>

						<div class="d-flex flex-row gap-2">
							<v-btn
								text
								class="cancel-btn"
								@click="closeModal"
							>
								Cancelar
							</v-btn>
							<v-btn
								class="confirm-btn"
								depressed
								:disabled="selectedFileIndex === null"
								@click="confirmSelection"
							>
								<v-icon left size="16">mdi-check</v-icon>
								Seleccionar
							</v-btn>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</modal>
	</div>
</template>

<script>
import { getProjectLineFiles } from '@/helpers/api/chats'
import _debounce from 'lodash/debounce'

export default {
	name: 'ChatProjectFileSelector',
	props: {
		projectId: {
			type: [Number, String],
			required: true
		}
	},
	data: () => ({
		indexModal: +new Date(),
		files: [],
		selectedFileIndex: null,
		loadingFiles: false,
		searchQuery: ''
	}),
	computed: {
		indexModalIndex() {
			const ordered = [...this.$store.state.general.openModalOrders].sort((a, b) => a - b)
			return ordered.indexOf(this.indexModal) + 10
		}
	},
	mounted() {
		this.$modal.show('project-file-selector')
		this.$store.commit('general/addOpenModalOrders', this.indexModal)
		this.fetchProjectFiles()
	},
	created() {
		this.debouncedSearch = _debounce(() => {
			this.selectedFileIndex = null
			this.fetchProjectFiles(this.searchQuery)
		}, 400)
	},
	beforeDestroy() {
		this.debouncedSearch.cancel()
	},
	methods: {
		async fetchProjectFiles(search = '') {
			this.loadingFiles = true
			try {
				const params = { project_line_id: this.projectId }
				if (search) params.search = search
				const response = await getProjectLineFiles(params)
				this.files = response.files || []
			} catch (error) {
				console.error('Error al cargar archivos del proyecto:', error)
			} finally {
				this.loadingFiles = false
			}
		},
		clearSearch() {
			this.searchQuery = ''
			this.selectedFileIndex = null
			this.fetchProjectFiles()
		},
		confirmSelection() {
			if (this.selectedFileIndex !== null) {
				const selectedFile = this.files[this.selectedFileIndex]
				this.$emit('file-selected', selectedFile)
				this.closeModal()
			}
		},
		formatFileSize(bytes) {
			if (!bytes || bytes === 0) return '0 B'
			const k = 1024
			const sizes = ['B', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
		},
		formatDate(dateStr) {
			if (!dateStr) return ''
			const date = new Date(dateStr)
			return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
		},
		getFileIcon(filename) {
			if (!filename) return 'mdi-file-outline'
			const ext = filename.split('.').pop().toLowerCase()
			const map = {
				pdf: 'mdi-file-pdf-box',
				doc: 'mdi-file-word-box', docx: 'mdi-file-word-box',
				xls: 'mdi-file-excel-box', xlsx: 'mdi-file-excel-box',
				ppt: 'mdi-file-powerpoint-box', pptx: 'mdi-file-powerpoint-box',
				jpg: 'mdi-file-image', jpeg: 'mdi-file-image', png: 'mdi-file-image', gif: 'mdi-file-image',
				zip: 'mdi-zip-box', rar: 'mdi-zip-box',
				txt: 'mdi-file-document-outline',
				mp4: 'mdi-file-video', mov: 'mdi-file-video',
				mp3: 'mdi-file-music',
			}
			return map[ext] || 'mdi-file-outline'
		},
		getFileIconColor(filename) {
			if (!filename) return '#888'
			const ext = filename.split('.').pop().toLowerCase()
			const colors = {
				pdf: '#E53935', doc: '#1565C0', docx: '#1565C0',
				xls: '#2E7D32', xlsx: '#2E7D32',
				ppt: '#E64A19', pptx: '#E64A19',
				jpg: '#7B1FA2', jpeg: '#7B1FA2', png: '#7B1FA2', gif: '#7B1FA2',
				zip: '#F57F17', rar: '#F57F17',
			}
			return colors[ext] || '#78909C'
		},
		getFileIconClass(filename) {
			if (!filename) return 'icon-default'
			const ext = filename.split('.').pop().toLowerCase()
			const classes = {
				pdf: 'icon-pdf', doc: 'icon-word', docx: 'icon-word',
				xls: 'icon-excel', xlsx: 'icon-excel',
				ppt: 'icon-ppt', pptx: 'icon-ppt',
				jpg: 'icon-img', jpeg: 'icon-img', png: 'icon-img',
				zip: 'icon-zip', rar: 'icon-zip',
			}
			return classes[ext] || 'icon-default'
		},
		closeModal() {
			this.$modal.hide('project-file-selector')
		}
	}
}
</script>

<style scoped>
/* Card */
.file-selector-card {
	border-radius: 12px !important;
	overflow: hidden;
	font-family: 'Inter', sans-serif;
}

/* Header */
.header-bar {
	background: #F8F7F7;
	cursor: grab;
	user-select: none;
}
.header-bar:active { cursor: grabbing; }

.header-icon-wrap {
	width: 38px;
	height: 38px;
	border-radius: 10px;
	background: #EDEBEB;
	display: flex;
	align-items: center;
	justify-content: center;
}

.header-title {
	font-size: 14px;
	font-weight: 700;
	color: #2C2929;
	line-height: 1.3;
}

.header-subtitle {
	font-size: 11px;
	color: #A09C9C;
	line-height: 1.2;
}

.header-divider {
	border-color: #EFEFEF !important;
}

.close-btn {
	color: #B0ABAB !important;
	transition: color 0.15s, background 0.15s;
}
.close-btn:hover {
	color: #524D4D !important;
	background: #EDEBEB !important;
}

/* Search */
.search-wrap { background: #fff; }

.search-field >>> .v-input__slot {
	border-radius: 8px !important;
	border-color: #E5E2E2 !important;
	font-size: 13px;
}
.search-field >>> .v-input__slot:hover {
	border-color: #B0ABAB !important;
}
.search-field >>> .v-input__slot.v-input--is-focused {
	border-color: #524D4D !important;
	box-shadow: 0 0 0 3px rgba(82, 77, 77, 0.08);
}
.search-field >>> .v-input__prepend-inner .v-icon {
	color: #B0ABAB;
	font-size: 18px;
}

/* File list */
.file-list-wrap {
	max-height: 320px;
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: #E0DCDC transparent;
}
.file-list-wrap::-webkit-scrollbar { width: 4px; }
.file-list-wrap::-webkit-scrollbar-track { background: transparent; }
.file-list-wrap::-webkit-scrollbar-thumb { background: #E0DCDC; border-radius: 4px; }

.file-list { background: transparent !important; }

.file-item {
	border-radius: 8px !important;
	border: 1.5px solid transparent;
	transition: background 0.13s, border-color 0.13s;
	min-height: 52px !important;
	padding: 0 10px !important;
}
.file-item:hover {
	background: #F8F7F7 !important;
	border-color: #E8E5E5;
}
.file-item--selected {
	background: #F2F0F0 !important;
	border-color: #C5C0C0 !important;
}

.file-icon-wrap {
	width: 32px;
	height: 32px;
	border-radius: 7px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #F2F0F0;
}
.icon-pdf   { background: #FFEBEE; }
.icon-word  { background: #E3F2FD; }
.icon-excel { background: #E8F5E9; }
.icon-ppt   { background: #FBE9E7; }
.icon-img   { background: #F3E5F5; }
.icon-zip   { background: #FFF8E1; }
.icon-default { background: #ECEFF1; }

.file-name {
	font-size: 13px !important;
	font-weight: 500 !important;
	color: #2C2929 !important;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.file-meta {
	font-size: 11px !important;
	color: #A09C9C !important;
	margin-top: 1px;
}

/* Skeleton loader */
.skeleton-item {
	border-radius: 8px;
	background: transparent;
}
.skeleton-icon {
	width: 32px;
	height: 32px;
	border-radius: 7px;
	background: linear-gradient(90deg, #F0EDEC 25%, #E8E5E4 50%, #F0EDEC 75%);
	background-size: 200% 100%;
	animation: shimmer 1.4s infinite;
	flex-shrink: 0;
}
.skeleton-line {
	height: 10px;
	border-radius: 5px;
	background: linear-gradient(90deg, #F0EDEC 25%, #E8E5E4 50%, #F0EDEC 75%);
	background-size: 200% 100%;
	animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
	0% { background-position: 200% 0; }
	100% { background-position: -200% 0; }
}

/* Empty state */
.empty-state { color: #C5C0C0; }
.empty-icon-wrap {
	width: 64px;
	height: 64px;
	border-radius: 16px;
	background: #F8F7F7;
	display: flex;
	align-items: center;
	justify-content: center;
}
.empty-title {
	font-size: 13px;
	font-weight: 600;
	color: #9E9898;
}
.empty-subtitle {
	font-size: 12px;
	color: #C5C0C0;
	text-align: center;
	max-width: 220px;
}

/* Footer */
.footer-bar {
	border-top: 1.5px solid #F0EDEC;
	background: #FAFAFA;
}
.footer-hint {
	font-size: 11.5px;
	color: #A09C9C;
	max-width: 200px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: flex;
	align-items: center;
}

.cancel-btn {
	font-size: 13px !important;
	font-weight: 500 !important;
	color: #8C8888 !important;
	text-transform: none !important;
	letter-spacing: 0 !important;
	border-radius: 8px !important;
	height: 36px !important;
}
.cancel-btn:hover { background: #F0EDEC !important; color: #524D4D !important; }

.confirm-btn {
	font-size: 13px !important;
	font-weight: 600 !important;
	color: #fff !important;
	background: #524D4D !important;
	text-transform: none !important;
	letter-spacing: 0 !important;
	border-radius: 8px !important;
	height: 36px !important;
	transition: background 0.15s, box-shadow 0.15s;
}
.confirm-btn:hover:not(.v-btn--disabled) {
	background: #3D3939 !important;
	box-shadow: 0 2px 8px rgba(82,77,77,0.25) !important;
}
.confirm-btn.v-btn--disabled {
	background: #D9D5D5 !important;
	color: #fff !important;
}

.gap-2 { gap: 8px; }
</style>